import React, { memo } from 'react';
import LinearProgress from '@mui/material/LinearProgress';
import AutoGraphOutlinedIcon from '@mui/icons-material/AutoGraphOutlined';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import EastOutlinedIcon from '@mui/icons-material/EastOutlined';
import FolderOffOutlinedIcon from '@mui/icons-material/FolderOffOutlined';
import PostAddOutlinedIcon from '@mui/icons-material/PostAddOutlined';
import { Box, Button, Text, Tooltip, useTheme } from '@ublend-npm/aulaui-core';
import useIsClamped from '@app/hooks/useIsClamped';
import SpaceCardBookIcon from './SpaceCardBookIcon';
import SpaceMetaPill from './SpaceMetaPill';
import SpaceCardContentRefreshSkeleton from './SpaceCardContentRefreshSkeleton';
import { SpaceWidgetCardData } from './SpaceWidget.types';
import { HOME_WIDGET_FONT_FAMILY } from '../homeWidgetTypography';
import { getSpaceWidgetColors } from './spaceWidgetColors';
import { useSpaceCardNavigation } from './useSpaceCardNavigation';
import {
  SPACE_CARD_BODY_BLOCK_GAP,
  SPACE_CARD_BODY_BLOCK_HEIGHT,
  SPACE_CARD_BORDER_RADIUS,
  SPACE_COMMUNITY_CARD_BG,
  SPACE_CARD_DESCRIPTION_COLOR,
  SPACE_CARD_DESCRIPTION_FONT_SIZE,
  SPACE_CARD_DESCRIPTION_FONT_WEIGHT,
  SPACE_CARD_DESCRIPTION_HEIGHT,
  SPACE_CARD_DESCRIPTION_LETTER_SPACING,
  SPACE_CARD_DESCRIPTION_LINE_HEIGHT,
  SPACE_CARD_HEADER_ROW_GAP,
  SPACE_CARD_HEADER_ROW_HEIGHT,
  SPACE_CARD_HEIGHT,
  SPACE_CARD_ICON_BUTTON_RADIUS,
  SPACE_CARD_ICON_FOCUS_RIPPLE_BG,
  SPACE_CARD_ICON_FOCUS_RIPPLE_SIZE,
  SPACE_CARD_INNER_GAP,
  SPACE_CARD_INNER_HEIGHT,
  SPACE_CARD_INNER_WIDTH,
  SPACE_CARD_PADDING_X,
  SPACE_CARD_PADDING_Y,
  SPACE_CARD_PROGRESS_SECTION_GAP,
  SPACE_CARD_PROGRESS_SECTION_HEIGHT,
  SPACE_ENGAGEMENT_CONTENT_GAP,
  SPACE_ENGAGEMENT_CONTENT_HEIGHT,
  SPACE_ENGAGEMENT_ICON_SIZE,
  SPACE_ENGAGEMENT_LINK_GAP,
  SPACE_ENGAGEMENT_LINK_HEIGHT,
  SPACE_ENGAGEMENT_LINK_MAX_WIDTH,
  SPACE_ENGAGEMENT_NO_ACTIVE_STUDENTS_LABEL,
  SPACE_ENGAGEMENT_TEXT_COLOR,
  SPACE_ENGAGEMENT_TEXT_FONT_SIZE,
  SPACE_ENGAGEMENT_TEXT_FONT_WEIGHT,
  SPACE_ENGAGEMENT_TEXT_HEIGHT,
  SPACE_ENGAGEMENT_TEXT_LETTER_SPACING,
  SPACE_ENGAGEMENT_TEXT_LINE_HEIGHT,
  SPACE_EMPTY_PROMPT_GAP,
  SPACE_EMPTY_PROMPT_HEIGHT,
  SPACE_EMPTY_PROMPT_ICON_SIZE,
  SPACE_EMPTY_PROMPT_EDUCATOR_LABEL,
  SPACE_EMPTY_PROMPT_STUDENT_LABEL,
  SPACE_EMPTY_PROMPT_TEXT_COLOR,
  SPACE_EMPTY_PROMPT_TEXT_FONT_SIZE,
  SPACE_EMPTY_PROMPT_TEXT_FONT_WEIGHT,
  SPACE_EMPTY_PROMPT_TEXT_HEIGHT,
  SPACE_EMPTY_PROMPT_TEXT_LETTER_SPACING,
  SPACE_EMPTY_PROMPT_TEXT_LINE_HEIGHT,
  SPACE_EMPTY_PROMPT_TEXT_WIDTH,
  SPACE_EMPTY_PROMPT_WIDTH,
  SPACE_CARD_TEXT_BLOCK_GAP,
  SPACE_CARD_TEXT_BLOCK_HEIGHT,
  SPACE_CARD_TITLE_DESC_BLOCK_HEIGHT,
  SPACE_CARD_TITLE_DESC_GAP,
  SPACE_CARD_TITLE_FONT_SIZE,
  SPACE_CARD_TITLE_FONT_WEIGHT,
  SPACE_CARD_TITLE_HEIGHT,
  SPACE_CARD_TITLE_LETTER_SPACING,
  SPACE_CARD_TITLE_LINE_HEIGHT,
  SPACE_CARD_WIDTH,
  SPACE_META_PILL_GAP,
  SPACE_META_PILLS_CONTAINER_HEIGHT,
  SPACE_META_PILLS_CONTAINER_WIDTH,
  SPACE_PROGRESS_BAR_HEIGHT,
  SPACE_PROGRESS_LABEL_FONT_SIZE,
  SPACE_PROGRESS_LABEL_HEIGHT,
  SPACE_PROGRESS_LABEL_LETTER_SPACING,
  SPACE_PROGRESS_LABEL_LINE_HEIGHT,
  SPACE_RESUME_BUTTON_FONT_SIZE,
  SPACE_RESUME_BUTTON_GAP,
  SPACE_RESUME_BUTTON_HEIGHT,
  SPACE_CONTINUE_LEARNING_BUTTON_LABEL,
  SPACE_EDIT_MATERIALS_BUTTON_LABEL,
  SPACE_OPEN_MATERIALS_BUTTON_LABEL,
  SPACE_START_LEARNING_BUTTON_LABEL,
  SPACE_RESUME_BUTTON_ICON_SIZE,
  SPACE_RESUME_BUTTON_LETTER_SPACING,
  SPACE_RESUME_BUTTON_LINE_HEIGHT,
  SPACE_RESUME_BUTTON_PADDING,
  SPACE_RESUME_DISABLED_LABEL,
} from './spaceWidgetConstants';

type SpaceCardProps = {
  space: SpaceWidgetCardData;
  tabIndex?: number;
};

const getMaterialActionLabel = ({
  role,
  hasAccessedMaterials,
}: SpaceWidgetCardData) => {
  if (role === 'student') {
    return hasAccessedMaterials
      ? SPACE_CONTINUE_LEARNING_BUTTON_LABEL
      : SPACE_START_LEARNING_BUTTON_LABEL;
  }

  return hasAccessedMaterials
    ? SPACE_EDIT_MATERIALS_BUTTON_LABEL
    : SPACE_OPEN_MATERIALS_BUTTON_LABEL;
};

const SpaceCard = ({ space, tabIndex = 0 }: SpaceCardProps) => {
  const theme = useTheme();
  const { palette, shape } = theme;
  const colors = getSpaceWidgetColors(palette);
  const isStudent = space.role === 'student';
  const isRefreshingCardContent = Boolean(space.isRefreshingCardContent);
  // No page/material to resume — show a role-specific empty prompt instead of
  // the description + progress/engagement rows.
  const isEmpty = !space.description;
  const emptyPromptLabel = isStudent
    ? SPACE_EMPTY_PROMPT_STUDENT_LABEL
    : SPACE_EMPTY_PROMPT_EDUCATOR_LABEL;
  // No materials to open for a student — keep the card but disable its action.
  const resumeDisabled = isStudent && isEmpty;
  const resumeButtonLabel = getMaterialActionLabel(space);
  const completionValue = space.completionPercentage ?? 0;
  const activeStudentsValue = space.activeStudentsPercentage ?? 0;
  const completionLabel = `${completionValue}% completed`;
  const formattedActiveStudentsPercentage = activeStudentsValue.toFixed(2);
  const activeStudentsLabel =
    activeStudentsValue === 0
      ? SPACE_ENGAGEMENT_NO_ACTIVE_STUDENTS_LABEL
      : `${formattedActiveStudentsPercentage}% Active Students`;
  const roleLabel = isStudent ? 'Student space' : 'Educator space';
  const showMetaPills = Boolean(space.academicYear || space.dateRange);
  // Announce the full card in visual order on focus: dates, name, description, progress.
  const cardAriaLabel = [
    space.academicYear,
    space.dateRange,
    `${space.title}, ${roleLabel}`,
    isRefreshingCardContent
      ? 'Updating space details'
      : isEmpty
      ? emptyPromptLabel
      : space.description,
    isRefreshingCardContent
      ? null
      : isEmpty
      ? null
      : isStudent
      ? completionLabel
      : activeStudentsLabel,
  ]
    .filter(Boolean)
    .join('. ');
  const {
    handleTitleClick,
    handleTitleKeyDown,
    handleResumeClick,
    handleEngagementClick,
    handleEngagementKeyDown,
  } = useSpaceCardNavigation(space);
  const [titleRef, isTitleClamped] = useIsClamped<HTMLElement>(space.title);

  return (
    <Box
      component="article"
      aria-label={cardAriaLabel}
      aria-busy={isRefreshingCardContent}
      tabIndex={tabIndex}
      sx={{
        width: SPACE_CARD_WIDTH,
        minWidth: SPACE_CARD_WIDTH,
        maxWidth: SPACE_CARD_WIDTH,
        height: SPACE_CARD_HEIGHT,
        flexShrink: 0,
        borderRadius: SPACE_CARD_BORDER_RADIUS,
        bgcolor: space.isCommunity ? SPACE_COMMUNITY_CARD_BG : colors.cardBg,
        color: colors.cardText,
        fontFamily: HOME_WIDGET_FONT_FAMILY,
        boxSizing: 'border-box',
        boxShadow: colors.cardShadow,
        scrollSnapAlign: 'start',
        outline: 'none',
        py: SPACE_CARD_PADDING_Y,
        px: SPACE_CARD_PADDING_X,
        '&:focus-visible': {
          outline: '2px solid #FFFFFF',
          outlineOffset: '2px',
          '& .space-card-icon::before': {
            content: '""',
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: SPACE_CARD_ICON_FOCUS_RIPPLE_SIZE,
            height: SPACE_CARD_ICON_FOCUS_RIPPLE_SIZE,
            borderRadius: SPACE_CARD_ICON_BUTTON_RADIUS,
            bgcolor: SPACE_CARD_ICON_FOCUS_RIPPLE_BG,
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',
          },
        },
      }}
    >
      <Box
        sx={{
          width: SPACE_CARD_INNER_WIDTH,
          height: SPACE_CARD_INNER_HEIGHT,
          display: 'flex',
          flexDirection: 'column',
          gap: SPACE_CARD_INNER_GAP,
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            width: SPACE_CARD_INNER_WIDTH,
            height: SPACE_CARD_BODY_BLOCK_HEIGHT,
            display: 'flex',
            flexDirection: 'column',
            gap: SPACE_CARD_BODY_BLOCK_GAP,
            boxSizing: 'border-box',
          }}
        >
          <Box
            sx={{
              width: SPACE_CARD_INNER_WIDTH,
              height: SPACE_CARD_HEADER_ROW_HEIGHT,
              display: 'flex',
              alignItems: 'center',
              gap: SPACE_CARD_HEADER_ROW_GAP,
              flexShrink: 0,
              minWidth: 0,
            }}
          >
            <SpaceCardBookIcon
              backgroundColor={colors.thumbnailBg}
              glyphColor={colors.iconGlyph}
              isCommunity={space.isCommunity}
            />
            {showMetaPills ? (
              <Box
                sx={{
                  width: SPACE_META_PILLS_CONTAINER_WIDTH,
                  height: SPACE_META_PILLS_CONTAINER_HEIGHT,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: SPACE_META_PILL_GAP,
                  flexShrink: 0,
                  minWidth: 0,
                  ml: 'auto',
                }}
              >
                {space.academicYear ? (
                  <SpaceMetaPill
                    label={space.academicYear}
                    backgroundColor={colors.chipBg}
                    textColor={colors.chipText}
                  />
                ) : null}
                {space.dateRange ? (
                  <SpaceMetaPill
                    label={space.dateRange}
                    backgroundColor={colors.chipBg}
                    textColor={colors.chipText}
                  />
                ) : null}
              </Box>
            ) : null}
          </Box>

          <Box
            sx={{
              width: SPACE_CARD_INNER_WIDTH,
              height: SPACE_CARD_TEXT_BLOCK_HEIGHT,
              display: 'flex',
              flexDirection: 'column',
              gap: SPACE_CARD_TEXT_BLOCK_GAP,
              minHeight: 0,
              boxSizing: 'border-box',
            }}
          >
            <Box
              sx={{
                width: SPACE_CARD_INNER_WIDTH,
                height: SPACE_CARD_TITLE_DESC_BLOCK_HEIGHT,
                display: 'flex',
                flexDirection: 'column',
                gap: SPACE_CARD_TITLE_DESC_GAP,
                flexShrink: 0,
                boxSizing: 'border-box',
              }}
            >
              <Tooltip
                title={space.title}
                placement="top"
                arrow
                disableHoverListener={!isTitleClamped}
                disableFocusListener={!isTitleClamped}
              >
                <Box
                  ref={titleRef}
                  component="h3"
                  role="link"
                  tabIndex={0}
                  onClick={handleTitleClick}
                  onKeyDown={handleTitleKeyDown}
                  sx={{
                    m: 0,
                    width: SPACE_CARD_INNER_WIDTH,
                    height: SPACE_CARD_TITLE_HEIGHT,
                    fontSize: SPACE_CARD_TITLE_FONT_SIZE,
                    fontWeight: SPACE_CARD_TITLE_FONT_WEIGHT,
                    lineHeight: SPACE_CARD_TITLE_LINE_HEIGHT,
                    letterSpacing: SPACE_CARD_TITLE_LETTER_SPACING,
                    color: colors.cardText,
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    boxSizing: 'border-box',
                    cursor: 'pointer',
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                    '&:focus-visible': {
                      outline: '2px solid #FFFFFF',
                      outlineOffset: '2px',
                    },
                  }}
                  aria-label={`Open ${space.title}`}
                >
                  {space.title}
                </Box>
              </Tooltip>

              {isRefreshingCardContent ? (
                <SpaceCardContentRefreshSkeleton
                  isStudent={isStudent}
                  variant="description"
                />
              ) : isEmpty ? (
                <Box
                  aria-hidden
                  sx={{
                    width: SPACE_EMPTY_PROMPT_WIDTH,
                    height: SPACE_EMPTY_PROMPT_HEIGHT,
                    display: 'flex',
                    alignItems: 'center',
                    gap: SPACE_EMPTY_PROMPT_GAP,
                    flexShrink: 0,
                    boxSizing: 'border-box',
                    color: SPACE_EMPTY_PROMPT_TEXT_COLOR,
                  }}
                >
                  {isStudent ? (
                    <FolderOffOutlinedIcon
                      sx={{
                        fontSize: SPACE_EMPTY_PROMPT_ICON_SIZE,
                        flexShrink: 0,
                      }}
                    />
                  ) : (
                    <PostAddOutlinedIcon
                      sx={{
                        fontSize: SPACE_EMPTY_PROMPT_ICON_SIZE,
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <Text
                    sx={{
                      m: 0,
                      width: SPACE_EMPTY_PROMPT_TEXT_WIDTH,
                      height: SPACE_EMPTY_PROMPT_TEXT_HEIGHT,
                      fontSize: SPACE_EMPTY_PROMPT_TEXT_FONT_SIZE,
                      fontWeight: SPACE_EMPTY_PROMPT_TEXT_FONT_WEIGHT,
                      lineHeight: SPACE_EMPTY_PROMPT_TEXT_LINE_HEIGHT,
                      letterSpacing: SPACE_EMPTY_PROMPT_TEXT_LETTER_SPACING,
                      color: SPACE_EMPTY_PROMPT_TEXT_COLOR,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {emptyPromptLabel}
                  </Text>
                </Box>
              ) : space.description ? (
                <Box
                  sx={{
                    width: SPACE_CARD_INNER_WIDTH,
                    height: SPACE_CARD_DESCRIPTION_HEIGHT,
                    display: 'flex',
                    alignItems: 'center',
                    flexShrink: 0,
                    boxSizing: 'border-box',
                  }}
                >
                  <Text
                    sx={{
                      m: 0,
                      width: '100%',
                      fontSize: SPACE_CARD_DESCRIPTION_FONT_SIZE,
                      fontWeight: SPACE_CARD_DESCRIPTION_FONT_WEIGHT,
                      lineHeight: SPACE_CARD_DESCRIPTION_LINE_HEIGHT,
                      letterSpacing: SPACE_CARD_DESCRIPTION_LETTER_SPACING,
                      color: SPACE_CARD_DESCRIPTION_COLOR,
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {space.description}
                  </Text>
                </Box>
              ) : (
                <Box
                  aria-hidden
                  sx={{
                    width: SPACE_CARD_INNER_WIDTH,
                    height: SPACE_CARD_DESCRIPTION_HEIGHT,
                    flexShrink: 0,
                  }}
                />
              )}
            </Box>

            {isRefreshingCardContent ? (
              <SpaceCardContentRefreshSkeleton
                isStudent={isStudent}
                variant="progress"
              />
            ) : isStudent && isEmpty ? (
              <Box
                aria-hidden
                sx={{
                  width: SPACE_CARD_INNER_WIDTH,
                  height: SPACE_CARD_PROGRESS_SECTION_HEIGHT,
                  flexShrink: 0,
                }}
              />
            ) : isStudent ? (
              <Box
                data-tour="home-space-card-progress"
                sx={{
                  width: SPACE_CARD_INNER_WIDTH,
                  height: SPACE_CARD_PROGRESS_SECTION_HEIGHT,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: SPACE_CARD_PROGRESS_SECTION_GAP,
                  flexShrink: 0,
                  boxSizing: 'border-box',
                }}
              >
                <Box
                  sx={{
                    width: SPACE_CARD_INNER_WIDTH,
                    height: SPACE_PROGRESS_BAR_HEIGHT,
                    flexShrink: 0,
                  }}
                >
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(Math.max(completionValue, 0), 100)}
                    aria-label={completionLabel}
                    sx={{
                      width: '100%',
                      height: SPACE_PROGRESS_BAR_HEIGHT,
                      borderRadius: 999,
                      bgcolor: colors.progressTrack,
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 999,
                        bgcolor: colors.progressFill,
                      },
                    }}
                  />
                </Box>
                <Text
                  sx={{
                    m: 0,
                    width: SPACE_CARD_INNER_WIDTH,
                    height: SPACE_PROGRESS_LABEL_HEIGHT,
                    fontSize: SPACE_PROGRESS_LABEL_FONT_SIZE,
                    fontWeight: 400,
                    lineHeight: SPACE_PROGRESS_LABEL_LINE_HEIGHT,
                    letterSpacing: SPACE_PROGRESS_LABEL_LETTER_SPACING,
                    color: colors.progressLabel,
                    display: 'flex',
                    alignItems: 'center',
                    flexShrink: 0,
                    boxSizing: 'border-box',
                  }}
                >
                  {completionLabel}
                </Text>
              </Box>
            ) : (
              <Box
                component="button"
                data-tour="home-space-card-engagement"
                onClick={handleEngagementClick}
                onKeyDown={handleEngagementKeyDown}
                aria-label={`${activeStudentsLabel}, open engagement`}
                sx={{
                  m: 0,
                  p: 0,
                  border: 'none',
                  bgcolor: 'transparent',
                  width: 'fit-content',
                  maxWidth: SPACE_ENGAGEMENT_LINK_MAX_WIDTH,
                  height: SPACE_ENGAGEMENT_LINK_HEIGHT,
                  display: 'flex',
                  alignItems: 'center',
                  gap: SPACE_ENGAGEMENT_LINK_GAP,
                  color: SPACE_ENGAGEMENT_TEXT_COLOR,
                  flexShrink: 0,
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontFamily: HOME_WIDGET_FONT_FAMILY,
                  boxSizing: 'border-box',
                  '&:hover': {
                    opacity: 0.9,
                  },
                  '&:focus-visible': {
                    outline: '2px solid #FFFFFF',
                    outlineOffset: '2px',
                  },
                }}
              >
                <Box
                  sx={{
                    height: SPACE_ENGAGEMENT_CONTENT_HEIGHT,
                    display: 'flex',
                    alignItems: 'center',
                    gap: SPACE_ENGAGEMENT_CONTENT_GAP,
                    minWidth: 0,
                    flex: '1 1 auto',
                  }}
                >
                  <AutoGraphOutlinedIcon
                    sx={{
                      fontSize: SPACE_ENGAGEMENT_ICON_SIZE,
                      flexShrink: 0,
                    }}
                  />
                  <Text
                    sx={{
                      m: 0,
                      minWidth: 0,
                      height: SPACE_ENGAGEMENT_TEXT_HEIGHT,
                      fontSize: SPACE_ENGAGEMENT_TEXT_FONT_SIZE,
                      fontWeight: SPACE_ENGAGEMENT_TEXT_FONT_WEIGHT,
                      lineHeight: SPACE_ENGAGEMENT_TEXT_LINE_HEIGHT,
                      letterSpacing: SPACE_ENGAGEMENT_TEXT_LETTER_SPACING,
                      color: SPACE_ENGAGEMENT_TEXT_COLOR,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {activeStudentsLabel}
                  </Text>
                </Box>
                <EastOutlinedIcon
                  sx={{
                    fontSize: SPACE_ENGAGEMENT_ICON_SIZE,
                    flexShrink: 0,
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>

        <Tooltip
          title={SPACE_RESUME_DISABLED_LABEL}
          placement="top"
          arrow
          disableHoverListener={!resumeDisabled}
          disableFocusListener={!resumeDisabled}
        >
          <Box data-tour="home-space-card-cta" sx={{ width: '100%' }}>
            <Button
              fullWidth
              kind="secondary"
              disabled={resumeDisabled}
              aria-label={
                resumeDisabled
                  ? `${resumeButtonLabel} — ${SPACE_RESUME_DISABLED_LABEL}`
                  : resumeButtonLabel
              }
              aria-disabled={resumeDisabled}
              endIcon={
                <ChevronRightOutlinedIcon
                  aria-hidden
                  sx={{ fontSize: SPACE_RESUME_BUTTON_ICON_SIZE }}
                />
              }
              onClick={handleResumeClick}
              sx={{
                width: SPACE_CARD_INNER_WIDTH,
                height: SPACE_RESUME_BUTTON_HEIGHT,
                minHeight: SPACE_RESUME_BUTTON_HEIGHT,
                p: SPACE_RESUME_BUTTON_PADDING,
                borderRadius: `${shape.borderRadius}px`,
                border: `1px solid ${colors.resumeButtonBorder}`,
                bgcolor: 'transparent',
                color: colors.cardText,
                textTransform: 'none',
                fontFamily: HOME_WIDGET_FONT_FAMILY,
                fontWeight: 400,
                fontSize: SPACE_RESUME_BUTTON_FONT_SIZE,
                lineHeight: SPACE_RESUME_BUTTON_LINE_HEIGHT,
                letterSpacing: SPACE_RESUME_BUTTON_LETTER_SPACING,
                boxShadow: 'none',
                justifyContent: 'center',
                gap: SPACE_RESUME_BUTTON_GAP,
                flexShrink: 0,
                '& .MuiButton-endIcon': {
                  m: 0,
                  ml: 0,
                },
                '&:hover': {
                  bgcolor: colors.resumeButtonHoverBg,
                  border: `1px solid ${colors.resumeButtonBorder}`,
                  boxShadow: 'none',
                },
                '&.Mui-disabled': {
                  opacity: 0.5,
                  color: colors.cardText,
                  border: `1px solid ${colors.resumeButtonBorder}`,
                  cursor: 'not-allowed',
                  pointerEvents: 'auto',
                },
              }}
            >
              {resumeButtonLabel}
            </Button>
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default memo(SpaceCard);
